import * as React from "react";
import { MuseumObject } from "@/backend/types/domain";
import { fixProxiedImageUrl } from "./MuseumObjectCard";
import { trackEvent } from "../lib/analytics";

const ENABLE_SOCIAL_SHARE =
  typeof window !== "undefined" && window.location.hostname === "localhost";

function getShareUrl(obj: MuseumObject): string {
  const baseUrl =
    window.location.hostname === "localhost"
      ? window.location.origin
      : "https://artifacts-index.surulere.dev";
  return `${baseUrl}/#artifact=${obj.id}`;
}

function getShareText(obj: MuseumObject): string {
  const title = obj.title || "African Artifact";
  const culture = obj.culture ? ` | ${obj.culture}` : "";
  return `${title}${culture} - African Artifacts Index`;
}

export const ShareButtons: React.FC<{ obj: MuseumObject }> = ({ obj }) => {
  const [copied, setCopied] = React.useState(false);
  const shareUrl = getShareUrl(obj);
  const shareText = getShareText(obj);
  const imageUrl = obj.images?.[0]?.url
    ? fixProxiedImageUrl(obj.images[0].url)
    : undefined;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: obj.title || "African Artifact",
          text: shareText,
          url: shareUrl,
        });
        trackEvent("share", { method: "native" });
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          console.error("Share failed:", err);
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      trackEvent("share", { method: "copy_link" });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const handleTwitterShare = () => {
    const twitterUrl = new URL("https://twitter.com/intent/tweet");
    twitterUrl.searchParams.set("text", shareText);
    twitterUrl.searchParams.set("url", shareUrl);
    trackEvent("share", { method: "x" });
    window.open(twitterUrl.toString(), "_blank", "noopener,noreferrer");
  };

  const handleInstagramShare = () => {
    const instagramUrl = `instagram://library?AssetPath=${encodeURIComponent(imageUrl || shareUrl)}`;
    trackEvent("share", { method: "instagram" });
    window.location.href = instagramUrl;
  };

  const buttonClass =
    "border border-theme-color text-theme-color hover:bg-theme-color p-1 px-2 text-sm cursor-pointer";

  return (
    <div className="flex flex-row flex-wrap gap-2 mt-2">
      {("share" in navigator) && (<button onClick={handleNativeShare} className={buttonClass}>
        SHARE
      </button>)}

      <button onClick={handleCopyLink} className={buttonClass}>
        {copied ? "COPIED!" : "COPY LINK"}
      </button>

      {ENABLE_SOCIAL_SHARE && (
        <>
          <button onClick={handleTwitterShare} className={buttonClass}>
            SHARE ON X
          </button>

          {imageUrl && (
            <button onClick={handleInstagramShare} className={buttonClass}>
              OPEN IN INSTAGRAM
            </button>
          )}
        </>
      )}
    </div>
  );
};
